/**
 * 채널 상세 페이지 모달 상태 관리 훅
 *
 * 채널 관련 모달 상태를 하나의 훅으로 통합 관리합니다.
 * - 채널 삭제
 * - YouTube 연동 해제
 */

'use client';

import { useReducer, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export type ChannelModalType = 'deleteChannel' | 'disconnectYoutube';

interface ChannelModalState {
  deleteChannel: boolean;
  disconnectYoutube: boolean;
  isConnecting: boolean;
}

type ChannelModalAction =
  | { type: 'OPEN_MODAL'; modal: ChannelModalType }
  | { type: 'CLOSE_MODAL'; modal: ChannelModalType }
  | { type: 'CLOSE_ALL' }
  | { type: 'SET_CONNECTING'; isConnecting: boolean };

// ============================================================================
// Initial State & Reducer
// ============================================================================

const initialState: ChannelModalState = {
  deleteChannel: false,
  disconnectYoutube: false,
  isConnecting: false,
};

function channelModalReducer(
  state: ChannelModalState,
  action: ChannelModalAction
): ChannelModalState {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, [action.modal]: true };

    case 'CLOSE_MODAL':
      return { ...state, [action.modal]: false };

    case 'CLOSE_ALL':
      return initialState;

    case 'SET_CONNECTING':
      return { ...state, isConnecting: action.isConnecting };

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useChannelModals() {
  const [state, dispatch] = useReducer(channelModalReducer, initialState);

  // 모달 열기/닫기
  const openModal = useCallback((modal: ChannelModalType) => {
    dispatch({ type: 'OPEN_MODAL', modal });
  }, []);

  const closeModal = useCallback((modal: ChannelModalType) => {
    dispatch({ type: 'CLOSE_MODAL', modal });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL' });
  }, []);

  // 연결 상태 관리
  const setConnecting = useCallback((isConnecting: boolean) => {
    dispatch({ type: 'SET_CONNECTING', isConnecting });
  }, []);

  return {
    // 모달 상태
    isOpen: {
      deleteChannel: state.deleteChannel,
      disconnectYoutube: state.disconnectYoutube,
    },

    // 연결 상태
    isConnecting: state.isConnecting,

    // 액션
    openModal,
    closeModal,
    closeAllModals,
    setConnecting,
  };
}

export type UseChannelModalsReturn = ReturnType<typeof useChannelModals>;
